/**
 * S3 Upload Utility
 *
 * Handles direct browser-to-S3 file uploads using presigned URLs
 */

import axios from 'axios';

/**
 * Upload file directly to S3 using presigned URL
 *
 * @param {File} file - File object from input
 * @param {string} fileType - Type: 'organization_icon', 'brand_icon', 'source_pdf', 'source_image', 'source_audio', 'source_video'
 * @param {object} metadata - Additional metadata (organization_id, brand_id, source_id, etc.)
 * @param {function} onProgress - Progress callback (percent: number) => void
 * @returns {Promise<{success: boolean, file_url?: string, error?: string}>}
 */
export async function uploadToS3(file, fileType, metadata = {}, onProgress = null) {
  try {
    // Step 1: Get presigned URL from backend
    const presignedResponse = await axios.post('/api/files/presigned-upload', {
      file_type: fileType,
      file_name: file.name,
      content_type: file.type,
      ...metadata
    }, {
      withCredentials: true
    });

    if (!presignedResponse.data.success) {
      return {
        success: false,
        error: presignedResponse.data.error || 'Failed to get upload URL'
      };
    }

    const { upload_url, fields, file_url } = presignedResponse.data;

    // Step 2: Upload file directly to S3
    return await uploadFileToS3(
      file,
      upload_url,
      fields,
      file_url,
      onProgress
    );

  } catch (error) {
    console.error('Error in uploadToS3:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Upload failed'
    };
  }
}

/**
 * Upload file to S3 using presigned POST
 *
 * @param {File} file - File to upload
 * @param {string} uploadUrl - Presigned upload URL
 * @param {object} fields - Additional fields for the POST request
 * @param {string} fileUrl - Final public URL of the file
 * @param {function} onProgress - Progress callback
 * @returns {Promise<{success: boolean, file_url?: string, error?: string}>}
 */
async function uploadFileToS3(file, uploadUrl, fields, fileUrl, onProgress) {
  return new Promise((resolve, reject) => {
    try {
      // Create FormData for S3 multipart upload
      const formData = new FormData();

      // Add S3 fields first (order matters for S3)
      Object.keys(fields).forEach(key => {
        formData.append(key, fields[key]);
      });

      // Add file last
      formData.append('file', file);

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            onProgress(percent);
          }
        });
      }

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({
            success: true,
            file_url: fileUrl
          });
        } else {
          resolve({
            success: false,
            error: `Upload failed with status ${xhr.status}`
          });
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        resolve({
          success: false,
          error: 'Network error during upload'
        });
      });

      // Handle timeout
      xhr.addEventListener('timeout', () => {
        resolve({
          success: false,
          error: 'Upload timed out'
        });
      });

      // Set timeout (10 minutes for large files)
      xhr.timeout = 600000;

      // Open and send request
      xhr.open('POST', uploadUrl);
      xhr.send(formData);

    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Validate file before upload
 *
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum file size in MB
 * @param {string[]} allowedTypes - Array of allowed MIME types
 * @returns {{valid: boolean, error?: string}}
 */
export function validateFile(file, maxSizeMB, allowedTypes) {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  // Check file type
  if (allowedTypes && allowedTypes.length > 0) {
    if (!allowedTypes.includes(file.type)) {
      const typesString = allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ');
      return {
        valid: false,
        error: `Invalid file type. Allowed types: ${typesString}`
      };
    }
  }

  // Check file size
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > maxSizeMB) {
    return {
      valid: false,
      error: `File size (${sizeMB.toFixed(2)}MB) exceeds maximum (${maxSizeMB}MB)`
    };
  }

  return { valid: true };
}

/**
 * Upload multiple files in parallel
 *
 * @param {File[]} files - Array of files to upload
 * @param {string} fileType - File type for all files
 * @param {object} metadata - Metadata for all files
 * @param {function} onProgress - Overall progress callback
 * @returns {Promise<Array<{success: boolean, file_url?: string, error?: string}>>}
 */
export async function uploadMultipleToS3(files, fileType, metadata = {}, onProgress = null) {
  const totalFiles = files.length;
  let completedFiles = 0;
  const results = [];

  const uploadPromises = files.map(async (file) => {
    const result = await uploadToS3(file, fileType, metadata, (percent) => {
      // Calculate overall progress
      if (onProgress) {
        const overallPercent = Math.round(
          ((completedFiles + (percent / 100)) / totalFiles) * 100
        );
        onProgress(overallPercent);
      }
    });

    completedFiles++;
    if (onProgress) {
      onProgress(Math.round((completedFiles / totalFiles) * 100));
    }

    return result;
  });

  return Promise.all(uploadPromises);
}

/**
 * Retry upload with exponential backoff
 *
 * @param {function} uploadFn - Upload function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} initialDelay - Initial delay in ms
 * @returns {Promise}
 */
export async function uploadWithRetry(uploadFn, maxRetries = 3, initialDelay = 1000) {
  let lastError;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await uploadFn();
      if (result.success) {
        return result;
      }
      lastError = result.error;
    } catch (error) {
      lastError = error.message || 'Upload failed';
    }

    // Don't wait after last attempt
    if (attempt < maxRetries - 1) {
      // Exponential backoff: 1s, 2s, 4s
      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  return {
    success: false,
    error: `Upload failed after ${maxRetries} attempts: ${lastError}`
  };
}

/**
 * Get file extension from filename
 *
 * @param {string} filename - Filename
 * @returns {string} - Extension (e.g., 'png', 'pdf')
 */
export function getFileExtension(filename) {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Format file size for display
 *
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size (e.g., '1.5 MB')
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Check if URL is an S3 URL (not base64)
 *
 * @param {string} url - URL to check
 * @returns {boolean} - True if S3 URL, false if base64 or other
 */
export function isS3Url(url) {
  if (!url) return false;
  return url.startsWith('https://') && (
    url.includes('.s3.') ||
    url.includes('s3.amazonaws.com')
  );
}

/**
 * Check if URL is a base64 data URI
 *
 * @param {string} url - URL to check
 * @returns {boolean} - True if base64 data URI
 */
export function isBase64Url(url) {
  if (!url) return false;
  return url.startsWith('data:');
}

export default {
  uploadToS3,
  validateFile,
  uploadMultipleToS3,
  uploadWithRetry,
  getFileExtension,
  formatFileSize,
  isS3Url,
  isBase64Url
};
